/**
 * Devises supportées côté frontend.
 * Aligné avec le backend (packages/nextjs/lib/constants/currencies.ts).
 *
 * EUR est la devise par défaut. Le `symbol` sert pour les préfixes d'input ;
 * l'affichage des montants formatés passe par formatCurrency (Intl).
 */
export interface CurrencyOption {
  code: string
  symbol: string
  /** Libellé affiché dans le sélecteur (nom court en anglais, suffisant pour 4 devises). */
  label: string
}

export const DEFAULT_CURRENCY = 'EUR'

export const CURRENCIES: CurrencyOption[] = [
  { code: 'EUR', symbol: '€', label: 'Euro' },
  { code: 'USD', symbol: '$', label: 'US Dollar' },
  { code: 'GBP', symbol: '£', label: 'British Pound' },
  { code: 'CHF', symbol: 'CHF', label: 'Swiss Franc' },
  { code: 'CAD', symbol: 'CA$', label: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', label: 'Australian Dollar' },
  { code: 'NZD', symbol: 'NZ$', label: 'New Zealand Dollar' },
  { code: 'JPY', symbol: '¥', label: 'Japanese Yen' },
  { code: 'CNY', symbol: 'CN¥', label: 'Chinese Yuan' },
  { code: 'HKD', symbol: 'HK$', label: 'Hong Kong Dollar' },
  { code: 'SGD', symbol: 'S$', label: 'Singapore Dollar' },
  { code: 'SEK', symbol: 'kr', label: 'Swedish Krona' },
  { code: 'NOK', symbol: 'kr', label: 'Norwegian Krone' },
  { code: 'DKK', symbol: 'kr', label: 'Danish Krone' },
  { code: 'PLN', symbol: 'zł', label: 'Polish Zloty' },
  { code: 'CZK', symbol: 'Kč', label: 'Czech Koruna' },
  { code: 'RON', symbol: 'lei', label: 'Romanian Leu' },
  { code: 'HUF', symbol: 'Ft', label: 'Hungarian Forint' },
  { code: 'AED', symbol: 'AED', label: 'UAE Dirham' },
  { code: 'SAR', symbol: 'SAR', label: 'Saudi Riyal' },
  { code: 'INR', symbol: '₹', label: 'Indian Rupee' },
  { code: 'BRL', symbol: 'R$', label: 'Brazilian Real' },
  { code: 'ZAR', symbol: 'R', label: 'South African Rand' },
  { code: 'MAD', symbol: 'MAD', label: 'Moroccan Dirham' },
  { code: 'TND', symbol: 'TND', label: 'Tunisian Dinar' },
]

const CURRENCY_BY_CODE: Record<string, CurrencyOption> = CURRENCIES.reduce(
  (acc, c) => {
    acc[c.code] = c
    return acc
  },
  {} as Record<string, CurrencyOption>
)

/** Renvoie le symbole d'une devise (ex: '€'), avec repli sur le code lui-même. */
export function getCurrencySymbol(currency?: string | null): string {
  if (!currency) return CURRENCY_BY_CODE[DEFAULT_CURRENCY].symbol
  const upper = String(currency).toUpperCase()
  return CURRENCY_BY_CODE[upper]?.symbol ?? upper
}

export function isSupportedCurrency(currency?: string | null): boolean {
  return !!currency && Object.prototype.hasOwnProperty.call(CURRENCY_BY_CODE, String(currency).toUpperCase())
}
